import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Leaflet chargé dynamiquement pour éviter le crash SSR / build
let MapContainer, TileLayer, Marker, Popup, L;
try {
  ({ MapContainer, TileLayer, Marker, Popup } = require('react-leaflet'));
  L = require('leaflet');
  require('leaflet/dist/leaflet.css');
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
} catch (e) {
  console.warn('Leaflet non disponible', e);
}

// Nom du lieu : title || json_data.name.value
const placeName = (place) =>
  place.title ||
  place.json_data?.name?.value ||
  place.gramps_id ||
  'Lieu inconnu';

// lat/long peuvent arriver en string depuis Postgres
const toFloat = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };

export const PlacesPage = () => {
  const [places,  setPlaces]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [expandedPlace, setExpandedPlace] = useState(null);
  const [eventsByPlace, setEventsByPlace] = useState({});
  const [loadingEvents, setLoadingEvents] = useState(null);

  useEffect(() => {
    fetchPlaces();
    // Petit délai pour laisser le DOM se stabiliser avant le rendu Leaflet
    const t = setTimeout(() => setMapReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  const fetchPlaces = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/places`, { withCredentials: true });
      // Normalise lat/long et filtre les lieux sans coordonnées
      const normalized = data
        .map(p => ({ ...p, lat: toFloat(p.lat), long: toFloat(p.long) }))
        .filter(p => p.lat !== null && p.long !== null);
      setPlaces(normalized);
    } catch (error) {
      console.error('Error fetching places:', error);
    } finally {
      setLoading(false);
    }
  };

  const center = places.length > 0 ? [places[0].lat, places[0].long] : [46.6, 2.3];

  const toggleEvents = async (handle) => {
    if (expandedPlace === handle) {
      setExpandedPlace(null);
      return;
    }
    setExpandedPlace(handle);
    if (!eventsByPlace[handle]) {
      setLoadingEvents(handle);
      try {
        const { data } = await axios.get(`${API_URL}/api/places/${handle}/events`, { withCredentials: true });
        setEventsByPlace(prev => ({ ...prev, [handle]: data }));
      } catch (error) {
        console.error('Error fetching place events:', error);
        setEventsByPlace(prev => ({ ...prev, [handle]: [] }));
      } finally {
        setLoadingEvents(null);
      }
    }
  };

  return (
    <div data-testid="places-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-forest mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Lieux géographiques
        </h1>
        <p className="text-stone-500">Visualisation des lieux sur la carte ({places.length} lieux géolocalisés)</p>
      </div>

      {loading ? (
        <div data-testid="places-loading" className="text-center py-12 text-stone-400">Chargement…</div>
      ) : places.length === 0 ? (
        <div data-testid="places-empty" className="text-center py-12 text-stone-400">
          Aucun lieu avec coordonnées trouvé
        </div>
      ) : (
        <>
          {/* Carte */}
          {MapContainer && mapReady && (
            <div className="bg-white border border-parchment-dark overflow-hidden" style={{ height: '500px' }}>
              <MapContainer center={center} zoom={6} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                {places.map((place) => (
                  <Marker key={place.handle} position={[place.lat, place.long]}>
                    <Popup>
                      <strong className="text-forest">{placeName(place)}</strong>
                      {place.code && <p className="text-xs text-stone-500 mt-1">{place.code}</p>}
                      <p className="text-xs text-stone-400">{place.lat.toFixed(4)}, {place.long.toFixed(4)}</p>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}

          {/* Grille des lieux */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {places.map((place) => (
              <div
                key={place.handle}
                data-testid={`place-card-${place.handle}`}
                className="bg-white border border-parchment-dark p-4 flex flex-col gap-3"
              >
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-terracotta mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-forest break-words">{placeName(place)}</h3>
                    {place.code && <p className="text-xs text-stone-500 mt-0.5">{place.code}</p>}
                    <p className="text-xs text-stone-400 mt-1">
                      {place.lat.toFixed(4)}, {place.long.toFixed(4)}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  data-testid={`place-events-toggle-${place.handle}`}
                  onClick={() => toggleEvents(place.handle)}
                  className="w-full flex items-center justify-between text-left text-xs font-medium text-forest border border-parchment-dark px-3 py-2 hover:bg-stone-50 active:bg-stone-100 transition-colors"
                >
                  <span>{expandedPlace === place.handle ? 'Masquer les événements' : 'Voir les événements'}</span>
                  <span className="text-stone-400 text-base leading-none">
                    {expandedPlace === place.handle ? '−' : '+'}
                  </span>
                </button>

                {expandedPlace === place.handle && (
                  <div data-testid={`place-events-${place.handle}`} className="border-t border-parchment-dark pt-2">
                    {loadingEvents === place.handle ? (
                      <p className="text-xs text-stone-400">Chargement…</p>
                    ) : (eventsByPlace[place.handle]?.length ?? 0) === 0 ? (
                      <p className="text-xs text-stone-400">Aucun événement enregistré</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {eventsByPlace[place.handle].map((ev) => (
                          <li key={ev.handle} className="text-xs text-stone-600 break-words">
                            <span className="font-medium text-forest">{ev.event_type}</span>
                            {ev.date_str && <span className="text-stone-400"> — {ev.date_str}</span>}
                            {ev.participants?.length > 0 && (
                              <span className="text-stone-500">
                                {' '}({ev.participants.map(p => `${p.given_name || ''} ${p.surname || ''}`.trim()).join(', ')})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};